import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { logger } from '../../utils/logger';

interface QuoteTimerProps {
  dueTimestamp: string;  // ISO timestamp
  onExpired: () => void;
  className?: string;
}

const QuoteTimer: React.FC<QuoteTimerProps> = ({
  dueTimestamp,
  onExpired,
  className = '',
}) => {
  const { t } = useTranslation();
  const [secondsRemaining, setSecondsRemaining] = useState<number>(0);

  useEffect(() => {
    const calculateRemaining = (): number => {
      const now = new Date().getTime();
      const due = new Date(dueTimestamp).getTime();
      const diff = Math.floor((due - now) / 1000);
      return Math.max(0, diff);
    };

    // Initial calculation
    const initial = calculateRemaining();
    setSecondsRemaining(initial);
    logger.log('[QuoteTimer] Initial seconds remaining:', initial);

    if (initial === 0) {
      logger.log('[QuoteTimer] Quote already expired');
      onExpired();
      return;
    }

    // Update every second
    const interval = setInterval(() => {
      const remaining = calculateRemaining();
      setSecondsRemaining(remaining);

      if (remaining === 0) {
        logger.log('[QuoteTimer] Quote expired');
        onExpired();
        clearInterval(interval);
      }
    }, 1000);

    return () => {
      clearInterval(interval);
    };
  }, [dueTimestamp, onExpired]);

  // Determine color based on urgency
  const getColorClass = (): string => {
    if (secondsRemaining <= 10) return 'quote-timer-critical';  // Red
    if (secondsRemaining <= 30) return 'quote-timer-warning';   // Yellow
    return 'quote-timer-normal';  // Green
  };

  // Format time display
  const formatTime = (): string => {
    if (secondsRemaining >= 60) {
      const minutes = Math.floor(secondsRemaining / 60);
      const seconds = secondsRemaining % 60;
      return `${minutes}m ${seconds}s`;
    }
    return `${secondsRemaining}s`;
  };

  return (
    <div className={`quote-timer ${getColorClass()} ${className}`}>
      <span className="quote-timer-icon">⏱</span>
      <span className="quote-timer-text">
        {t('tradeBooking.quote.expiresIn', 'Quote expires in')}: {formatTime()}
      </span>
    </div>
  );
};

export default QuoteTimer;
